import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import {
  getCurrentCitySlug,
  shouldShowOnDemandSearchCard
} from 'src/components/SearchManager/SearchList'
import { shouldShowLogisticsCard } from 'src/components/SearchManager/shouldShowLogisticsCard'
import { useFilterProvider } from 'src/context/FilterProvider'

const OnDemandRecoveryCard = dynamic(() => import('./OnDemandRecoveryCard'))
const LogisticsSearchCard = dynamic(() => import('./LogisticsSearchCard'))

/**
 * Empty-state surface for the search results grid. After the empty-state
 * harmonization (May 2026) this component owns ONLY the on-demand recovery
 * hero. The previous "Sin coincidencias / ¿Quieres ver más opciones?" heading
 * was absorbed into `<CaptureLead>` (its city-aware H3 already plays that
 * role), removing a redundant visual beat between two card surfaces.
 *
 * Renders the cream hero card when the current pillar/city/coords resolve to
 * a covered, partner-active on-demand market; falls back to the Soluciones
 * Logísticas (3PL) recovery card on a zero-results B2B-pillar search; renders
 * `null` otherwise so the parent's `<CaptureLead>` becomes the first beat
 * with no phantom vertical space. The two gates are mutually exclusive by
 * pillar in practice (SelfStorage vs Warehouse/Industrial), so only one
 * recovery card ever renders — on-demand takes priority when both somehow
 * resolve (e.g. a multi-select /search filter spanning both).
 */
type IEmptyStateProps = {
  /** Coordinates the current search resolved to — see the same prop on
   *  `SearchList`. Zero-result searches are exactly where a stale `?city=`
   *  used to do the most damage (nothing on screen contradicts the card), so
   *  the recovery hero gets the same coords-first gate as the grid card. */
  searchCenter?: { lat?: number | null; lng?: number | null }
}

function EmptyState({ searchCenter }: IEmptyStateProps) {
  const router = useRouter()
  const { filters } = useFilterProvider()

  const gateLat = searchCenter?.lat ?? filters.lat
  const gateLng = searchCenter?.lng ?? filters.lng
  const onDemandResolution = useMemo(
    () =>
      shouldShowOnDemandSearchCard(
        router.query.pillar,
        getCurrentCitySlug(router.query.city, filters.city),
        filters.spaceTypes,
        gateLat ?? undefined,
        gateLng ?? undefined
      ),
    [
      router.query.pillar,
      router.query.city,
      filters.city,
      filters.spaceTypes,
      gateLat,
      gateLng
    ]
  )

  const showLogisticsCard = useMemo(
    () => shouldShowLogisticsCard(router.query.pillar, filters.spaceTypes),
    [router.query.pillar, filters.spaceTypes]
  )

  if (onDemandResolution) {
    return (
      <div className="pb-8 md:pb-10">
        <OnDemandRecoveryCard
          canonicalCity={onDemandResolution.city}
          cityConfig={onDemandResolution.config}
        />
      </div>
    )
  }

  if (showLogisticsCard) {
    return (
      <div className="pb-8 md:pb-10">
        <LogisticsSearchCard variant="empty" />
      </div>
    )
  }

  return null
}

export default EmptyState
